/**
 * Workspace navigation, rendered in the body of the inset sidebar. Nav
 * items are gated by `useAbility()` so a member without permission to read
 * agents never sees the Agents item.
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 4.4`.
 *
 * Built on the `@cometchat-ai/ui-dashboard` sidebar primitives: each item is
 * a `SidebarMenuButton` rendering a router `Link`, with the collapsed-rail
 * tooltip and active highlight driven by the primitive. Active state is
 * resolved with `useMatch` against the item's resolved path.
 *
 * The icon imports are M3 placeholders for the M7-M10 surfaces — the route
 * targets themselves are stubbed and CG-5/6 will replace placeholder pages
 * with the real ones; M7+ owns its own routes.
 */

import { useAbility, useAuth } from "@cometchat-ai/auth/client";
import { flagEnabled, isHelpdeskOnlyPrincipal } from "@cometchat-ai/auth/shared";
import {
  cn,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@cometchat-ai/ui-dashboard";
import {
  Activity,
  Building2,
  Cpu,
  Database,
  Flag,
  KeyRound,
  MessageSquareHeart,
  MessagesSquare,
  NavCards,
  NavContextVariables,
  NavConversations,
  NavMcp,
  NavMembers,
  NavTables,
  NavTools,
  Network,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
  Workflow,
  Zap,
  type LucideIcon,
} from "./icons";
import { useTranslation } from "react-i18next";
import { Link, useMatch, useResolvedPath } from "react-router";

import type { Action, Subject } from "@cometchat-ai/auth/client";

/**
 * Translation keys are typed via module augmentation in `src/i18n/types.ts`,
 * which makes `t(...)` reject unknown strings. The nav item descriptors
 * hold dynamic keys, so we union them into a literal type so the dynamic
 * `t(item.labelKey)` call below stays type-safe.
 */
type NavLabelKey =
  | "nav.home"
  | "nav.platform"
  | "nav.agents"
  | "nav.workflows"
  | "nav.contacts"
  | "nav.kb"
  | "nav.tables"
  | "nav.tools"
  | "nav.cards"
  | "nav.actions"
  | "nav.channels"
  | "nav.agentops"
  | "nav.conversations"
  | "nav.runs"
  | "nav.inbox"
  | "nav.helpdesk"
  | "nav.helpdeskMembers"
  | "nav.helpdeskRoles"
  | "nav.helpdeskTeams"
  | "nav.feedback"
  | "nav.workspace"
  | "nav.members"
  | "nav.authentication"
  | "nav.memory"
  | "nav.contextVariables"
  | "nav.roles"
  | "nav.featureFlags"
  | "nav.settings"
  | "nav.mcp"
  | "nav.internal"
  | "nav.staffConsole"
  | "nav.organization"
  | "nav.orgWorkspaces";

interface NavItemSpec {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  labelKey: NavLabelKey;
  /** Explicit data-testid suffix; defaults to `to`. */
  testId?: string;
  /** When set, the item only renders if the user can perform `[action, subject]`. */
  ability?: readonly [Action, Subject];
}

/**
 * Internal staff / FDE console. Absolute path (it lives OUTSIDE `/w/:slug`),
 * shown only to `platform:staff`. Gated on the permission, not an ability —
 * a workspace admin's broad abilities must not reveal it.
 */
const STAFF_NAV_ITEM: NavItemSpec = {
  to: "/staff/customers",
  icon: Building2,
  labelKey: "nav.staffConsole",
  testId: "staff-console",
};

/** Product surfaces. */
const NAV_ITEMS: ReadonlyArray<NavItemSpec> = [
  { to: "agents", icon: Sparkles, labelKey: "nav.agents", ability: ["read", "Agent"] },
  { to: "workflows", icon: Workflow, labelKey: "nav.workflows", ability: ["read", "Agent"] },
  { to: "contacts", icon: Users, labelKey: "nav.contacts", ability: ["read", "Agent"] },
  { to: "channels", icon: Network, labelKey: "nav.channels", ability: ["read", "Agent"] },
  { to: "agentops", icon: Activity, labelKey: "nav.agentops", ability: ["read", "Run"] },
  {
    to: "conversations",
    icon: NavConversations,
    labelKey: "nav.conversations",
    ability: ["read", "Conversation"],
  },
  { to: "kb", icon: Database, labelKey: "nav.kb", ability: ["read", "KbDocument"] },
  { to: "tables", icon: NavTables, labelKey: "nav.tables", ability: ["read", "DataTable"] },
  { to: "tools", icon: NavTools, labelKey: "nav.tools", ability: ["read", "Agent"] },
  { to: "cards", icon: NavCards, labelKey: "nav.cards", ability: ["read", "Agent"] },
  { to: "actions", icon: Zap, labelKey: "nav.actions", ability: ["read", "Agent"] },
  { to: "mcp", icon: NavMcp, labelKey: "nav.mcp", ability: ["read", "Agent"] },
];

/**
 * Helpdesk — the operator surface plus the desk's own delegated management
 * (helpdesk-team unit). `Inbox` is the live monitor (any operator); `Members`
 * + `Roles` are the `helpdesk_admin` delegation views, gated on the scoped
 * `helpdesk:manage-*` abilities so a plain `helpdesk_member` sees only Inbox.
 */
const HELPDESK_NAV_ITEMS: ReadonlyArray<NavItemSpec> = [
  {
    to: "helpdesk",
    end: true,
    icon: MessagesSquare,
    labelKey: "nav.inbox",
    ability: ["monitor", "Conversation"],
  },
  // Customers' ratings on agent replies — desk/quality review work.
  {
    to: "feedback",
    icon: MessageSquareHeart,
    labelKey: "nav.feedback",
    ability: ["read", "Feedback"],
  },
  {
    to: "helpdesk/members",
    icon: NavMembers,
    labelKey: "nav.helpdeskMembers",
    ability: ["read-members", "Helpdesk"],
  },
  {
    to: "helpdesk/roles",
    icon: ShieldCheck,
    labelKey: "nav.helpdeskRoles",
    ability: ["manage-roles", "Helpdesk"],
  },
  // Routing teams (teams-routing LLD): admin surface, same gate as the API.
  {
    to: "helpdesk/teams",
    icon: Users,
    labelKey: "nav.helpdeskTeams",
    ability: ["configure", "HandoffEvent"],
  },
];

/**
 * Workspace administration — promoted out of the old `/settings` tab
 * wrapper into their own sidebar group (Workspace / Members /
 * Authentication). The Overview landing was dropped entirely.
 */
const SETTINGS_NAV_ITEMS: ReadonlyArray<NavItemSpec> = [
  // Grouped by concern: general → access/security → agent data → advanced.
  { to: "workspace", icon: Settings, labelKey: "nav.workspace", ability: ["read", "Workspace"] },
  { to: "members", icon: NavMembers, labelKey: "nav.members", ability: ["read", "Member"] },
  { to: "roles", icon: ShieldCheck, labelKey: "nav.roles", ability: ["read", "OrgMember"] },
  { to: "auth", icon: KeyRound, labelKey: "nav.authentication" },
  { to: "memory", icon: Cpu, labelKey: "nav.memory" },
  { to: "context-variables", icon: NavContextVariables, labelKey: "nav.contextVariables" },
  { to: "feature-flags", icon: Flag, labelKey: "nav.featureFlags", ability: ["read", "OrgMember"] },
];

/**
 * Account-level administration. Its own group, shown only to org admins /
 * staff (gated on the management ability `read OrgMember`, which a plain org
 * member lacks). Data is org-scoped (`/api/org/*`); these pages live under
 * `/w/:slug` only to reuse the settings shell + ability provider.
 */
const ORG_NAV_ITEMS: ReadonlyArray<NavItemSpec> = [
  {
    to: "organization/members",
    icon: Building2,
    labelKey: "nav.organization",
    ability: ["read", "OrgMember"],
  },
  {
    to: "organization/workspaces",
    icon: Network,
    labelKey: "nav.orgWorkspaces",
    ability: ["read", "OrgMember"],
  },
];

/* Quiet nav rows — active reads as a soft muted fill (no white chip/shadow);
   hover is a fainter muted tint. Attio keeps nav restrained. */
const NAV_BUTTON_CLASS =
  "text-[13px] text-[color:var(--color-fg-muted)] hover:!bg-[color:color-mix(in_oklch,var(--color-fg)_4%,transparent)] data-[active=true]:bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] data-[active=true]:font-medium data-[active=true]:text-foreground";

const NAV_GROUP_LABEL_CLASS = "text-2xs font-medium text-[color:var(--color-fg-subtle)]";

function NavItem({ item }: { item: NavItemSpec }) {
  const { t } = useTranslation("workspace");
  const resolved = useResolvedPath(item.to);
  const match = useMatch({ path: resolved.pathname, end: item.end ?? false });
  const label = t(item.labelKey);
  const active = Boolean(match);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={label} className={NAV_BUTTON_CLASS}>
        <Link
          to={item.to}
          data-testid={`nav-${item.testId ?? (item.to === "." ? "home" : item.to)}`}
        >
          <item.icon
            variant={active ? "Bold" : undefined}
            className={cn(
              active
                ? "text-[color:color-mix(in_oklch,var(--color-fg)_55%,transparent)]"
                : "text-[color:var(--color-fg-muted)]",
            )}
            aria-hidden="true"
          />
          <span>{label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function SidebarNav() {
  const { t } = useTranslation("workspace");
  const ability = useAbility();
  const auth = useAuth();
  const isPlatformStaff = auth.permissions.includes("platform:staff");
  // Workflows surface is flag-gated (`surface.workflows`, default OFF) so
  // environments without the workflow infra never show it. UI-only hide —
  // the routes/endpoints stay permission-gated regardless.
  const workflowsEnabled = flagEnabled(auth, "surface.workflows");

  const visible = (items: ReadonlyArray<NavItemSpec>) =>
    items.filter(
      (item) =>
        (item.to !== "workflows" || workflowsEnabled) &&
        (!item.ability || ability.can(...item.ability)),
    );

  const orgItems = visible(ORG_NAV_ITEMS);
  const helpdeskItems = visible(HELPDESK_NAV_ITEMS);

  // A helpdesk-only operator's whole job is the desk: render just the Helpdesk
  // group and suppress Platform / Settings / Org (helpdesk-team unit). Their
  // permissions still power the Inbox — this only trims the NAV. Admins hold
  // slugs outside the desk set, so they never hit this branch.
  const helpdeskOnly = isHelpdeskOnlyPrincipal(auth.permissions);
  if (helpdeskOnly) {
    return (
      <SidebarGroup className="px-2" aria-label={t("nav.helpdesk")}>
        <SidebarGroupLabel className={NAV_GROUP_LABEL_CLASS}>{t("nav.helpdesk")}</SidebarGroupLabel>
        <SidebarMenu>
          {helpdeskItems.map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
        </SidebarMenu>
      </SidebarGroup>
    );
  }

  return (
    <>
      <SidebarGroup className="px-2" aria-label={t("nav.platform")}>
        <SidebarGroupLabel className={NAV_GROUP_LABEL_CLASS}>{t("nav.platform")}</SidebarGroupLabel>
        <SidebarMenu>
          {visible(NAV_ITEMS).map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
        </SidebarMenu>
      </SidebarGroup>

      {helpdeskItems.length > 0 ? (
        <>
          <SidebarSeparator className="hidden group-data-[collapsible=icon]:block" />
          <SidebarGroup className="px-2" aria-label={t("nav.helpdesk")}>
            <SidebarGroupLabel className={NAV_GROUP_LABEL_CLASS}>
              {t("nav.helpdesk")}
            </SidebarGroupLabel>
            <SidebarMenu>
              {helpdeskItems.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </>
      ) : null}

      {/* Group labels disappear in the collapsed icon rail; keep a divider. */}
      <SidebarSeparator className="hidden group-data-[collapsible=icon]:block" />

      <SidebarGroup className="px-2" aria-label={t("nav.settings")}>
        <SidebarGroupLabel className={NAV_GROUP_LABEL_CLASS}>{t("nav.settings")}</SidebarGroupLabel>
        <SidebarMenu>
          {visible(SETTINGS_NAV_ITEMS).map((item) => (
            <NavItem key={item.to} item={item} />
          ))}
        </SidebarMenu>
      </SidebarGroup>

      {orgItems.length > 0 ? (
        <>
          <SidebarSeparator className="hidden group-data-[collapsible=icon]:block" />
          <SidebarGroup className="px-2" aria-label={t("nav.organization")}>
            <SidebarGroupLabel className={NAV_GROUP_LABEL_CLASS}>
              {t("nav.organization")}
            </SidebarGroupLabel>
            <SidebarMenu>
              {orgItems.map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </>
      ) : null}

      {isPlatformStaff ? (
        <>
          <SidebarSeparator className="hidden group-data-[collapsible=icon]:block" />
          <SidebarGroup className="px-2" aria-label={t("nav.internal")}>
            <SidebarGroupLabel className={NAV_GROUP_LABEL_CLASS}>
              {t("nav.internal")}
            </SidebarGroupLabel>
            <SidebarMenu>
              <NavItem item={STAFF_NAV_ITEM} />
            </SidebarMenu>
          </SidebarGroup>
        </>
      ) : null}
    </>
  );
}
